import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AuthService,
  LoggerService,
  shareReplayRefCount,
  StateService,
  Unity4Claim,
} from '@adreno-common';
import { TeleworkerState } from '@app/core/models';
import { CoreService } from '@app/core/services';
import { environment } from '@environments/environment';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
  catchError,
  concatMap,
  mergeAll,
  pairwise,
} from 'rxjs/operators';
import {
  WorkMenuComponent,
  UnavailablePresenceStateComponent,
  VerifyCloseModalComponent,
  NailupModalContentComponent,
  NailupDroppedModalComponent,
} from '../components';
import { Answers, AnswerType, Master, Queue } from '../code';
import {
  ListScheduledCampaignsVm,
  emptyAnswers,
  GetAutoAnswerVm,
  RecordSubmitDto,
  QuestionAnswerDto,
  GetNameandnumberVm,
  RescheduleNameandnumberCommand,
  GetPreviewRecordVm,
  ListCampaignQuestionsVm,
  RecordQAHistoryRequest,
  GetUserOutcomeStatsVm,
  MediaDto,
  ScriptContext,
  TeleworkerValues,
  GetStandbyLimitVm,
  ActionDto,
  ListPositionsVm,
  GetPredictiveRecordVm,
  ActionRecordsVm,
  ListActionTypeForCampaignVm,
  ListCampaignQueuesVm,
  Tenant,
  ListNameAndNumberCountsVm,
  ListCampaignFinishDateVm,
  GetAgentStatusVm,
  RetryBufferItem,
  CampaignDialType,
} from '../models';
import {
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import { NgxDeepEqualsPureService } from 'ngx-deep-equals-pure';
import { Router } from '@angular/router';
import { State } from './work.state';
import { ScriptWorkerService } from './script-worker.service';
import { WorkReducerService } from './work-reducer.service';
import { initalState, TimezoneService } from '.';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UserProfileService } from '@app/core/services/user-profile.service';
import { ToastrService } from 'ngx-toastr';
import { TasksComponent } from '../components/tasks.component';
import { NailupTimeoutModalComponent } from '../components/nailup-timeout-modal.component';
import { CallbackService } from './callback.service';
import { RecordService } from './record.service';
import { formatDate } from '@angular/common';
import { PresenceService } from '@app/presence/services';

@Injectable({
  providedIn: 'root',
})
export class WorkService extends StateService<State> {
  private readonly campaignApi = `${environment.apiPath}/campaign`;
  private readonly failedUploadLocalStorageKey = 'failedUpload';
  private readonly finishDateSub = new BehaviorSubject<Date | undefined>(
    undefined
  );
  private readonly queueApi = `${environment.apiPath}/queue`;
  private readonly recordsApi = `${environment.apiPath}/records`;
  private readonly refreshImmediately = new Subject<void>();
  private refreshSubs?: Subscription[];
  private readonly retryBufferIntervalMs = 30000; //30sec
  private readonly retryBufferLifetime = 24 * 60 * 60 * 1000; //24 hrs
  private readonly retryBufferLocalStorageKey = 'retryBuffer';
  private uatActionType: string = '';
  private uatCampaignId: number = 0;
  private uatScriptVersion: number = 0;
  private readonly usersApi = `${environment.apiPath}/users`;

  action$ = this.select((state) => state.action);
  allowBack$ = this.select((state) => state.allowBack);
  answers$ = this.select((state) => state.answers);
  autoAnswer$ = this.select((state) => state.autoAnswer);
  auxState$ = this.select((state) => state.auxState);
  campaignDialType$ = this.select((state) => state.dialType);
  campaignFinishDate$ = this.finishDateSub.asObservable();
  campaignId$ = this.select((state) => state.campaignId); //this.coreSvc.campaignId$;
  campaignScriptOpen$ = this.select((state) => state.campaignScriptOpen);
  closeDisable$ = this.select((state) => state.closeDisable);
  closeScript$ = this.select((state) => state.closeScript);
  defaultTenantId$ = this.select(
    (state) => state.defaultTenantId,
    () =>
      this.http
        .get<Tenant>(`${this.usersApi}/defaultTenant`)
        .subscribe((vm) => (this.state = { defaultTenantId: vm.tenantId }))
  ).pipe(filter((dtid) => !!dtid));
  hasIBScheduled$ = this.select((state) => state.hasIBScheduled);
  hasNNRecords$ = this.select((state) => state.hasNameAndNumber);
  hasNNScheduled$: Observable<boolean> | undefined;
  history$ = this.select((state) => state.history);
  ibLaunched$ = this.select((state) => state.ibLaunched);
  inUnavailableState = false;
  isFastVoicemail$ = this.select((state) => state.isFastVoicemail);
  killMonitor$ = this.select((state) => state.action?.killMonitor);
  master$ = this.select((state) => state.master);
  nailupTimeLimit$ = this.select((state) => state.nailupTimeLimit);
  noNavBack$ = this.select((state) => state.noNavBack);
  set omniChannel(v: boolean) {
    this.coreSvc.state = TeleworkerState.Work;
    this.coreSvc.campaignId = undefined;
    this.state = { omniChannel: v };
  }
  obContinue$ = this.select((state) => state.obContinue);
  obLaunched$ = this.select((state) => state.obLaunched);
  obMessage$ = this.select((state) => state.obMessage);
  omniChannel$ = this.select((state) => state.omniChannel);
  oncall$ = this.select((state) => state.oncall);
  outcome$ = this.select((state) => state.action?.outcome);
  positions$ = this.select(
    (state) => state.positions,
    () =>
      this.http
        .get<ListPositionsVm>(`${this.recordsApi}/positions`)
        .subscribe((vm) => (this.state = { positions: vm.positions }))
  );

  postCallSurveyCampaign$ = this.select(
    (state) => state.postCallSurveyCampaign
  );
  postCallSurveyCancel$ = this.select((state) => state.postCallSurveyCancel);
  postCallSurveyData$ = this.select((state) => state.postCallSurveyData);
  postCallSurveyPhone$ = this.select((state) => state.postCallSurveyPhone);
  postCallSurveyRecordId$ = this.select(
    (state) => state.postCallSurveyRecordId
  );

  qNo$ = this.select((state) => state.qNo);
  // recordingPlaceolder = '';
  recordingPlaceolders: string[] = [];

  scheduledCampaigns$ = this.select((state) => state.scheduledCampaigns);
  scriptClosed: boolean = false;
  scriptData$ = this.select((state) => ({
    system: state.system,
    master: state.master,
    action: state.action,
    answers: state.answers,
    variables: state.variables,
  })).pipe(
    distinctUntilChanged((prev, curr) =>
      this.deepEqualsSvc.deepEquals(prev, curr)
    )
  );
  scriptDisabled$ = this.select((state) => state.scriptDisable);
  scriptInputs$ = this.select((state) => state.qInputs).pipe(
    withLatestFrom(this.select((state) => state.answers)),
    map(([inputs, answers]) => ({ inputs, answers }))
  );
  scriptText$ = this.select((state) => ({
    html: state.qText,
    context: {
      action: state.action,
      answers: state.answers,
      master: state.master,
      tw: {
        extraData: state.system?.extraData,
      } as TeleworkerValues,
      variables: state.variables,
    } as ScriptContext,
    blade: state.blade,
  })).pipe(
    distinctUntilChanged((prev, curr) =>
      this.deepEqualsSvc.deepEquals(prev, curr)
    ),
    switchMap((scriptText) =>
      this.userSvc.userProfile$.pipe(
        map((userProfile) => ({ scriptText, userProfile }))
      )
    ),
    map(({ scriptText, userProfile }) => ({
      ...scriptText,
      ...{
        context: {
          ...scriptText.context,
          tw: {
            ...scriptText.context.tw,
            userEmail1: userProfile.email,
            userEmail2: userProfile.email2,
            userFirst: userProfile.first,
            userLast: userProfile.last,
          } as TeleworkerValues,
        } as ScriptContext,
      },
    }))
  );

  standbyLimit$ = this.select((state) => state.standbyLimit);
  tasksPresented$ = this.select((state) => state.tasksPresented);
  teleworkerState$ = this.coreSvc.state$;
  testMode$ = this.select((state) => state.testMode);
  transferCall$ = this.select((state) => state.transferCall);
  userOutcomeStats$ = this.select((state) => state.userOutcomeStats);
  variables$ = this.select((state) => state.variables);
  voicemailMsg$ = this.select((state) => state.voicemailMsg);
  waitingforob$ = this.select((state) => state.waitingforob);
  waitingobcall$ = this.select((state) => state.waitingobcall);

  constructor(
    private coreSvc: CoreService,
    private http: HttpClient,
    private logger: LoggerService,
    private deepEqualsSvc: NgxDeepEqualsPureService,
    private router: Router,
    private scriptWorker: ScriptWorkerService,
    private modalSvc: NgbModal,
    private tzSvc: TimezoneService,
    private toastr: ToastrService,
    reducer: WorkReducerService,
    private userSvc: UserProfileService,
    private callbackSvc: CallbackService,
    private recordSvc: RecordService,
    private presenceSvc: PresenceService,
    private authService: AuthService
  ) {
    super(initalState, reducer);

    this.initUploadRetry();

    this.hasNNScheduled$ = this.scheduledCampaigns$.pipe(
      map((sc) => sc?.some((c) => c.actionType.startsWith('NN')) || false)
    );

    this.killMonitor$
      .pipe(
        startWith(undefined),
        pairwise(),
        filter(([prev, curr]) => {
          if (prev === undefined && curr === undefined) return false;
          if (prev === undefined && curr == false) return false;
          return true;
        }),
        map(([prev, curr]) => !!curr)
      )
      .subscribe((killMonitor) => {
        this.presenceSvc.killMonitor({ killMonitor });
      });

    this.coreSvc.campaignId$.subscribe((campaignId) => {
      this.state = { campaignId: campaignId };

      if (campaignId! > 0) {
        // work out if we have voicemail msgs for this campaign
        let msgCount = 0;
        const { msgs } = this.state;
        if (msgs !== undefined) {
          if (msgs.filter((msg) => msg.campaignId === campaignId)) {
            if (
              msgs.filter((msg) => msg.campaignId === campaignId).length > 0
            ) {
              msgCount = msgs.filter((msg) => msg.campaignId === campaignId)[0]
                .msgCount;
            }
          }
        }

        this.state = { hasNameAndNumber: msgCount > 0 };
        this.getFinishDateForCampaign(campaignId!);
      }
    });

    this.coreSvc.state$.subscribe((twState) => {
      let auxState = this.state.auxState;
      let oncall = this.state.oncall;
      this.inUnavailableState = false;

      if (
        twState === TeleworkerState.InboundConnected ||
        twState === TeleworkerState.OutboundConnected ||
        twState === TeleworkerState.HoldIB ||
        twState === TeleworkerState.HoldOB
      ) {
        oncall = true;
        auxState = false;
      } else if (twState === TeleworkerState.OutboundWaiting) {
        //do nothing
      } else if (
        twState === TeleworkerState.Unavailable ||
        twState === TeleworkerState.UnavailableIB ||
        twState === TeleworkerState.UnavailableOB
      ) {
        this.inUnavailableState = true;
      } else {
        oncall = false;

        // additional auxState exclusions
        if (
          twState === TeleworkerState.WrapIB ||
          twState === TeleworkerState.WrapOB ||
          twState === TeleworkerState.CallDialling ||
          twState === TeleworkerState.Standby ||
          twState === TeleworkerState.TransferIB ||
          twState === TeleworkerState.TransferOB ||
          twState === TeleworkerState.DiallerLoggedIn
        ) {
          auxState = false;
        }
      }

      this.state = { oncall: oncall, auxState: auxState };
    });

    this.master$
      .pipe(
        map((master) => master?.recordId),
        distinctUntilChanged()
      )
      .subscribe((recordId) => (this.coreSvc.recordId = recordId));

    this.master$
      .pipe(
        map((master) => master?.recordId),
        distinctUntilChanged(),
        switchMap((recordId) => {
          if (recordId) {
            return this.getHistory$(recordId);
          } else {
            return of(undefined);
          }
        })
      )
      .subscribe((history) => {
        this.state = { history };
      });

    this.closeScript$
      .pipe(filter((closeScript) => !!closeScript))
      .subscribe((closePage) => {
        this.state = { closeScript: undefined };
        if (closePage === true) router.navigate(['work', 'history']);
        else router.navigate(['work', closePage]);
      });

    this.postCallSurveyData$
      .pipe(withLatestFrom(this.master$, this.defaultTenantId$))
      .subscribe(([pcsdata, master, tid]) => {
        this.state = {
          postCallSurveyPhone: master?.phone1,
          postCallSurveyRecordId: master?.recordId,
          postCallSurveyCampaign: master?.campaignId,
        };
      });

    this.presenceSvc.forceLogout$.subscribe(() => this.unsetWork());

    this.initScriptWorker();
    this.initBeforeUnload();
    this.initCallbacks();
    this.loadAutoAnswer();
    this.loadStandbyLimit();
  }

  private addRetryBuffer(record: RecordSubmitDto, attempt: number, error: any) {
    var retryBufferJSON = localStorage.getItem(this.retryBufferLocalStorageKey);
    var retryBuffer = retryBufferJSON
      ? (JSON.parse(retryBufferJSON) as RetryBufferItem[])
      : [];
    const retryItem = {
      attempts: attempt,
      lastAttempt: new Date(),
      lastError: error,
      record,
    } as RetryBufferItem;
    retryBuffer.push(retryItem);
    this.logger.warn('WorkService', 'addRetryBuffer', {
      length: retryBuffer.length,
      recordid: retryItem.record.recordId,
    })();
    localStorage.setItem(
      this.retryBufferLocalStorageKey,
      JSON.stringify(retryBuffer)
    );
  }

  private beforeUnload = (ev: BeforeUnloadEvent) => {
    this.logger.warn('WorkService', 'beforeUnload')();
    ev.preventDefault();
    ev.returnValue = 'Are you sure?'; //the message is not displayed by any modern borwser
    return false;
  };

  private fetchFailUpload(): RetryBufferItem | undefined {
    var json = localStorage.getItem(this.failedUploadLocalStorageKey);
    localStorage.removeItem(this.failedUploadLocalStorageKey);
    var failedUpload = json ? (JSON.parse(json) as RetryBufferItem) : undefined;
    return failedUpload;
  }

  private fetchRetryBuffer(includeFailedUpload: boolean): RetryBufferItem[] {
    var retryBufferJSON = localStorage.getItem(this.retryBufferLocalStorageKey);
    localStorage.removeItem(this.retryBufferLocalStorageKey);

    var retryBuffer = retryBufferJSON
      ? (JSON.parse(retryBufferJSON) as RetryBufferItem[])
      : [];

    //add legacy submission to beginning of array
    var legacyJSON = localStorage.getItem('resubmitrecord');
    localStorage.removeItem('resubmitrecord');
    var legacy = legacyJSON
      ? (JSON.parse(legacyJSON) as RecordSubmitDto)
      : undefined;
    if (legacy) {
      retryBuffer.unshift({
        attempts: 1,
        record: legacy,
      } as RetryBufferItem);
    }

    if (includeFailedUpload) {
      //add failedUpload if exists (from before refresh)
      var failedUploadItem = this.fetchFailUpload();
      if (failedUploadItem) {
        retryBuffer.push(failedUploadItem);
      }
    }

    // we want to ensure that the record isn't older than lifetime limit.
    const checkTime = Date.now() - this.retryBufferLifetime;

    return retryBuffer.filter((item) => {
      return (
        item.record.action?.endTime &&
        new Date(item.record.action?.endTime).getTime() > checkTime
      );
    });
  }

  private getCampaignQueues$(campaignId: number): Observable<Queue[]> {
    return this.http
      .get<ListCampaignQueuesVm>(`${this.campaignApi}/${campaignId}/queues`)
      .pipe(map((vm) => vm.queues));
  }

  private getHistory$(recordId: number): Observable<ActionDto[]> {
    return this.http
      .get<ActionRecordsVm>(`${this.recordsApi}/action/${recordId}`)
      .pipe(map((vm) => vm.actions));
  }

  private getPredictiveRecord$(campaignId: number, recordId: number) {
    return this.http
      .get<GetPredictiveRecordVm>(
        `${this.queueApi}/predictive/${campaignId}/${recordId}`
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logger.error('WorkService', 'getPredictiveRecord', {
            error,
            campaignId,
            recordId,
          })();
          return of(undefined);
        })
      );
  }

  private initBeforeUnload() {
    this.select((state) => state.allowRefresh).subscribe((allowRefresh) => {
      if (allowRefresh) {
        window.removeEventListener('beforeunload', this.beforeUnload);
      } else {
        window.addEventListener('beforeunload', this.beforeUnload);
      }
    });
  }

  private initCallbacks() {
    //can't reference WorkService from within CallbackService, so update it with the data it needs
    this.campaignId$.subscribe((campaignId) => {
      this.callbackSvc.currentCampaignId = campaignId;
    });
    this.scheduledCampaigns$.subscribe((scheduledCampaigns) => {
      this.callbackSvc.scheduledCampaigns = scheduledCampaigns;
    });
  }

  private initScriptWorker() {
    this.scheduledCampaigns$
      .pipe(filter((campaigns) => !!campaigns && campaigns.length > 0))
      .subscribe((campaigns) =>
        this.scriptWorker.loadWorkers(
          campaigns!.map((c) => ({
            campaignId: c.campaignId,
            versionId: c.versionId,
          }))
        )
      );
  }

  private initUploadRetry() {
    timer(2000, this.retryBufferIntervalMs)
      .pipe(
        map((ct) => this.fetchRetryBuffer(ct == 0)),
        filter((retryBuffer) => !!retryBuffer && retryBuffer.length > 0),
        tap((retryBuffer) =>
          this.logger.warn('WorkService', 'initUploadRetry', {
            length: retryBuffer.length,
            records: retryBuffer.map((ri) => ri.record.recordId),
          })()
        ),
        map((retryBuffer) => from(retryBuffer)),
        mergeAll(),
        concatMap((retryItem) => {
          this.logger.debug('WorkService', 'initUploadRetry concatMap', {
            recordid: retryItem.record.recordId,
          })();
          return this.uploadRecord(
            retryItem.record,
            retryItem.attempts,
            false
          ).pipe(
            map((success) => ({ success, retryItem })),
            catchError(() => of({ success: false, retryItem }))
          );
        })
      )
      .subscribe((result) => {
        this.logger.warn('WorkService', 'initUploadRetry', {
          success: result.success,
          recordid: result.retryItem.record.recordId,
        })();
        if (result.success) {
          this.toastr.success(
            `${result.retryItem?.record.recordId} - ${result.retryItem?.record.master?.first} ${result.retryItem?.record.master?.last} successfully uploaded.`,
            'Record Submisssion'
          );
        }
      });
  }

  private loadScheduledCampaigns() {
    return this.http
      .get<ListScheduledCampaignsVm>(`${this.usersApi}/campaigns`)
      .pipe(
        withLatestFrom(this.campaignScriptOpen$),
        tap(([vm, campaignScriptOpen]) => {
          if (!campaignScriptOpen) {
            // if the campaign script is open we don't want to reload the campaigns as the script version may have changed.
            this.setScheduledCampaigns(vm);
          } else {
            // set the pendingCampaigns state value and in the submitRecord we will reload
            // and blank out.
            this.state = { pendingCampaigns: vm };
          }
        }),
        map(() => void 0)
      );
  }

  private reloadCampaignsOnInterval() {
    if (!this.refreshSubs) {
      this.refreshSubs = [];
      this.refreshSubs.push(
        merge(
          interval(5 * 60 * 1000), //5 mins
          this.refreshImmediately.asObservable()
        )
          .pipe(
            startWith(-1),
            switchMap(() => this.loadScheduledCampaigns())
          )
          .subscribe()
      );

      this.refreshSubs.push(
        merge(
          this.coreSvc.state$.pipe(
            map((state) =>
              [TeleworkerState.AvailableIB, TeleworkerState.Tasks].includes(
                state!
              )
                ? 1
                : 5
            ), //when in AvailableIB or Tasks refresh every 1 min, otherwise every 5 mins
            distinctUntilChanged(),
            switchMap((intervalMins) => interval(intervalMins * 60 * 1000))
          ),
          this.refreshImmediately.asObservable()
        )
          .pipe(
            startWith(-1),
            switchMap(() => {
              this.logger.debug(
                'WorkService',
                'refreshScheduledCampaigns',
                'loadUserCallbacks'
              )();
              return this.callbackSvc.loadUserCallbacks();
            })
          )
          .subscribe()
      );
    } else {
      this.reloadCampaigns();
    }
  }

  private setFailedUpload(
    record: RecordSubmitDto,
    attempt: number,
    error: any
  ) {
    const retryItem = {
      attempts: attempt,
      lastAttempt: new Date(),
      lastError: error,
      record,
    } as RetryBufferItem;
    this.logger.warn('WorkService', 'setFailedUpload', {
      recordid: retryItem.record.recordId,
    })();
    localStorage.setItem(
      this.failedUploadLocalStorageKey,
      JSON.stringify(retryItem)
    );
  }

  private setScheduledCampaigns(vm: ListScheduledCampaignsVm) {
    this.state = {
      scheduledCampaigns: vm.campaigns.map((c) => ({
        ...c,
        shifts: c.shifts.map((s) => ({
          startUtc: new Date(`${s.startUtc}Z`),
          finishUtc: new Date(`${s.finishUtc}Z`),
          hardFinishUtc: new Date(`${s.hardFinishUtc}Z`),
        })),
      })),
      system: {
        ...this.state.system,
        queues: vm.queues,
        routingAreas: vm.routingAreas,
      },
      queues: vm.queues,
      routingAreas: vm.routingAreas,
      hasIBScheduled: vm.campaigns.some((c) => c.actionType.startsWith('IB')),
    };

    this.loadNNData();

    this.state = { pendingCampaigns: undefined };
  }

  private uploadRecord(
    record: RecordSubmitDto,
    attempt: number = 0,
    userRetry: boolean = false
  ): Observable<boolean> {
    return this.authService.hasClaim$(Unity4Claim.UseSignalrSubmit).pipe(
      take(1),
      withLatestFrom(
        this.authService.hasClaim$(Unity4Claim.Base64EncodeRecord)
      ),
      switchMap(([useSignalr, base64]) => {
        if (useSignalr && this.presenceSvc.withinMessageLimit(record, base64)) {
          return this.presenceSvc.submitRecord(record, base64);
        }
        if (base64) {
          const decodedString = JSON.stringify(record);
          const encodedString = btoa(decodedString);
          return this.http
            .put(`${this.recordsApi}/base64`, { encodedString })
            .pipe(map(() => true));
        }
        return this.http
          .put(`${this.recordsApi}`, record)
          .pipe(map(() => true));
      }),
      catchError((error) => {
        this.toastr.error(
          `${record.recordId} - ${record.master?.first} ${
            record.master?.last
          } failed to upload. ${
            userRetry
              ? 'Please wait a moment and try again.'
              : 'System will automatically retry'
          }`,
          'Record Submisssion'
        );
        this.logger.error('WorkService', 'uploadRecord()', {
          error,
          record,
          attempt,
        })();

        if (userRetry) {
          //user can retry manually
          this.setFailedUpload(record, attempt + 1, error);
        } else {
          //add to automated retry
          this.addRetryBuffer(record, attempt + 1, error);
        }

        return throwError(error);
      })
    );
  }

  addRecording(filename: string) {
    if (!!filename) {
      /*const { contact } = this.state;

      if (contact !== undefined) {
        this.state = {
          contact: {
            ...contact,
            recording: [...(contact.recording || []), filename],
          },
        };
      } else {
        // just put it in the placeholder
        // this will be for namandnumber clicktocalls where we don't have a record yet.
        //this.recordingPlaceolder = filename;
        this.recordingPlaceolders.push(filename);
      }*/

      // just store it in the placeHolder as we will add them all on submit.
      this.recordingPlaceolders.push(filename);
    }
  }

  backToPortal() {
    this.unsetWork();
    this.exit();
    this.routeToNews();
  }

  backToUnloaded() {
    this.closeRecord();
    //this.unsetWork();

    if (this.state.obLaunched) {
      this.exit();
    } else {
      // we are on an IB campaign and we are wanting to keep the nailup active.
      this.coreSvc.campaignId = 0;
    }

    this.router.navigate(['/work']);
  }

  closeRecord() {
    // this.coreSvc.state = TeleworkerState.Work;
    this.scriptClosed = true;

    this.state = {
      blade: undefined,
      recordId: undefined,
      system: undefined,
      master: undefined,
      action: undefined,
      answers: undefined,
      variables: undefined,
      history: undefined,
      contact: undefined,
      qNo: undefined,
      qText: undefined,
      qInputs: undefined,
      closeDisable: undefined,
      closeScript: undefined,
      scriptDisable: false,
      noNavBack: undefined,
      //obLaunched: false,
      ibLaunched: false,
      inputs: undefined,
      campaignScriptOpen: false,
    };
  }

  closeScript(page: 'contact' | 'history') {
    this.state = {
      system: {
        ...this.state.system,
        ...{ closeScript: page },
      },
    };
  }

  disableClose() {
    this.state = {
      system: {
        ...this.state.system,
        ...{ closeDisable: true },
      },
    };
  }

  enableClose() {
    this.state = {
      system: {
        ...this.state.system,
        ...{ closeDisable: false },
      },
    };
  }

  exit() {
    this.coreSvc.campaignId = 0;
    this.state = { obLaunched: false, ibLaunched: false };
    // this.unsetWork();
    this.coreSvc.state = TeleworkerState.DiallerLogout;
  }

  getActionTypeForCampaign(campaignId: number) {
    return this.http.get<ListActionTypeForCampaignVm>(
      `${this.campaignApi}/${campaignId}/actiontype`
    );
  }

  getAgentStatus$(
    hostname: string,
    port: number
  ): Observable<GetAgentStatusVm> {
    return this.http.get<GetAgentStatusVm>(`${this.usersApi}/agentstatus`, {
      params: {
        hostname,
        port,
      },
    });
  }

  getFinishDateForCampaign(campaignId: number) {
    this.http
      .get<ListCampaignFinishDateVm>(
        `${this.campaignApi}/${campaignId}/finishdate`
      )
      .subscribe((dte) => {
        this.finishDateSub.next(dte.finishDate);
      });
  }

  getNextUATRecord() {
    this.coreSvc.scriptVersion = this.uatScriptVersion;
    this.coreSvc.campaignId = this.uatCampaignId;

    if (this.uatActionType.startsWith('IB')) {
      this.popTestScript(this.uatCampaignId, this.uatScriptVersion, 1, true);
    } else {
      this.getPreviewRecord(this.uatCampaignId, true);
    }
  }

  getPreviewRecord(campaignId: number, uatFlag: boolean = false) {
    var recordid: number = 0;

    this.http
      .get<GetPreviewRecordVm>(`${this.recordsApi}/preview/${campaignId}`)
      .subscribe((vm) => {
        recordid = vm.recordId;
        if (recordid === 0) {
          this.toastr.error('No records in queue or queue not loaded');
          this.state = { obLaunched: false, dialType: undefined };
          this.coreSvc.campaignId = 0;
          if (!uatFlag) this.coreSvc.state = TeleworkerState.Work;
        } else {
          if (!uatFlag) this.coreSvc.state = TeleworkerState.Preview;
          else this.coreSvc.state = TeleworkerState.UAT;

          this.popScript(
            campaignId,
            recordid,
            undefined,
            vm.queueId.toString(),
            undefined,
            undefined,
            undefined,
            true
          );

          this.router.navigate(['/work', 'contact']);
        }
      });
  }

  isUserScheduled(campaignId: number) {
    return this.scheduledCampaigns$.pipe(
      take(1),
      map((scs) => scs?.find((sc) => sc.campaignId === campaignId)),
      map((campaign) => {
        if (!campaign) return false;
        const now = new Date();
        const isSched =
          campaign.shifts?.some(
            (shift) => shift.startUtc <= now && now <= shift.hardFinishUtc
          ) || false;
        return isSched;
      })
    );
  }

  launchCampaign(campaignId: number, dialType: CampaignDialType) {
    this.state = { omniChannel: false, dialType };
    this.logger.debug('WorkService', 'lauchCampaign', {
      campaignId,
      dialType,
    })();

    const { hasIBScheduled } = this.state;

    let isFastVoicemail: boolean = false;
    let nailupTimeLimit: number = -1;
    let msgCount: number = 0;

    this.scheduledCampaigns$.pipe(take(1)).subscribe((scheduledCampaigns) => {
      const campaign = scheduledCampaigns?.find(
        (c) => c.campaignId === campaignId
      );

      this.coreSvc.scriptVersion = campaign?.versionId;

      if (campaign?.isFastVoicemail)
        isFastVoicemail = campaign?.isFastVoicemail;

      if (campaign?.nailupTimeLimit)
        nailupTimeLimit = campaign?.nailupTimeLimit;

      this.state = {
        isFastVoicemail: isFastVoicemail,
        nailupTimeLimit: nailupTimeLimit,
      };
    });

    if (dialType === 'Preview' || dialType === 'Predictive') {
      this.state = { obLaunched: true, ibLaunched: false };
    } else {
      this.state = { obLaunched: false };
    }

    this.coreSvc.campaignId = campaignId;

    if (!hasIBScheduled || dialType !== 'Preview') {
      /// we were in an IB campaign but have launched an OB one.
      this.coreSvc.state = TeleworkerState.DiallerLogin;
    } else {
      if (dialType === 'Preview') {
        // move to blended preview state
        // this triggers the cancelGetNextNumber and opening new preview record
        // in diallerSvc
        this.coreSvc.state = TeleworkerState.BlendedPreview;
      }
    }

    //this.listCampaignUserSchedule(campaignId);
  }

  launchCampaignIB(campaignId: number, dialType: CampaignDialType) {
    this.coreSvc.campaignId = campaignId;
    this.state = {
      omniChannel: false,
      dialType,
      obLaunched: false,
      ibLaunched: true,
    };

    this.popScript(campaignId);
  }

  launchCampaignUAT(
    campaignId: number,
    dialType: CampaignDialType,
    actionType: string
  ) {
    this.state = { omniChannel: false, dialType };
    this.logger.debug('WorkService', 'lauchCampaignUAT', {
      campaignId,
      dialType,
    })();

    let isFastVoicemail: boolean = false;
    let nailupTimeLimit: number = -1;

    this.scheduledCampaigns$.pipe(take(1)).subscribe((scheduledCampaigns) => {
      const campaign = scheduledCampaigns?.find(
        (c) => c.campaignId === campaignId
      );

      this.coreSvc.scriptVersion = campaign?.versionId;
      this.uatScriptVersion = campaign?.versionId!;

      if (campaign?.isFastVoicemail)
        isFastVoicemail = campaign?.isFastVoicemail;

      if (campaign?.nailupTimeLimit)
        nailupTimeLimit = campaign?.nailupTimeLimit;

      this.state = {
        isFastVoicemail: isFastVoicemail,
        nailupTimeLimit: nailupTimeLimit,
      };
    });

    if (dialType === 'Preview' || dialType === 'Predictive') {
      this.state = { obLaunched: true, ibLaunched: false };
    } else {
      this.state = { obLaunched: false };
    }

    if (actionType.startsWith('IB'))
      this.popTestScript(campaignId, this.coreSvc.scriptVersion!, 1, true);
    else this.getPreviewRecord(campaignId, true);

    this.uatActionType = actionType;
    this.uatCampaignId = campaignId;

    this.coreSvc.campaignId = campaignId;
    this.coreSvc.state = TeleworkerState.UAT;
  }

  listCampaignQuestions(campaignId: number, recordId: number) {
    return this.http
      .get<ListCampaignQuestionsVm>(
        `${this.campaignApi}/${campaignId}/${recordId}/faq`
      )
      .pipe(
        map((vm) => vm.questions),
        shareReplayRefCount()
      );
  }

  loadAutoAnswer() {
    this.http
      .get<GetAutoAnswerVm>(`${this.usersApi}/autoanswer`)
      .subscribe((vm) => {
        //this.state = { autoAnswer: false };
        this.state = { autoAnswer: vm.inboundAutoAnswer };
      });
  }

  loadNNData() {
    const { scheduledCampaigns } = this.state;

    if (scheduledCampaigns && scheduledCampaigns.length > 0) {
      const campaignids = scheduledCampaigns.map((x) => x.campaignId).join(',');

      this.http
        .get<ListNameAndNumberCountsVm>(
          `${this.usersApi}/nameandnumber/${campaignids}`
        )
        .pipe(withLatestFrom(this.coreSvc.campaignId$))
        .subscribe(([vm, campaignId]) => {
          let msgCount = 0;
          let useCampaignCount = false;
          let allCampaignCount = 0;

          if (vm.msgCounts.length > 0) {
            allCampaignCount = vm.msgCounts
              .map((item) => item.msgCount)
              .reduce((prev, next) => prev + next);
          }

          if (campaignId !== undefined && campaignId > 0) {
            // we have a selected campaign so we want the messgae count for the campaign
            useCampaignCount = true;
            if (vm.msgCounts.filter((msg) => msg.campaignId === campaignId)) {
              vm.msgCounts
                .filter((msg) => msg.campaignId === campaignId)
                .forEach((campMsg) => {
                  msgCount = campMsg.msgCount;
                });
            }
          }

          this.state = {
            msgs: vm.msgCounts,
            hasNameAndNumber: useCampaignCount
              ? msgCount > 0
              : allCampaignCount > 0,
          };
        });
    }
  }

  loadStandbyLimit() {
    this.http
      .get<GetStandbyLimitVm>(`${this.usersApi}/standbylimit`)
      .subscribe((vm) => {
        this.state = { standbyLimit: vm.standbyLimit };
      });
  }

  loadStats() {
    this.http
      .get<GetUserOutcomeStatsVm>(`${this.usersApi}/stats`)
      .subscribe((vm) => {
        this.state = { userOutcomeStats: vm.children };
      });
  }

  loadVoiceMailMsg(campaignId: number) {
    this.state = { voicemailMsg: undefined };
    this.http
      .get<GetNameandnumberVm>(`${this.recordsApi}/nameandnumber/${campaignId}`)
      .subscribe((vm) => {
        this.state = { voicemailMsg: vm };
      });
  }

  msgContacted(sessionid: string) {
    this.http
      .put<any>(`${this.recordsApi}/nameandnumber/${sessionid}`, '')
      .subscribe();
  }

  navBack() {
    const state = this.state;
    if (!!state.system?.qHistory && state.system?.qHistory.length > 1) {
      this.state = {
        system: {
          ...state.system,
          ...{
            navToQ: state.system!.qHistory![1],
            qHistory: state.system!.qHistory!.slice(2),
            navedBack: true,
          },
        },
      };
    }
  }

  navNext(qAnswers?: AnswerType) {
    const { qNo, system, master, action, answers, variables } = this.state;
    this.state = {
      system: {
        ...system,
        ...{
          navToQ: '?',
          navedBack: undefined,
        },
      },
      answers: [
        ...answers!.slice(0, qNo!),
        qAnswers,
        ...answers!.slice(qNo! + 1),
      ] as Answers,
    };
  }

  navigateToScript() {
    this.router.navigate(['/work', 'script']);
  }

  navigateToWork() {
    this.router.navigate(['/work']);
  }

  openNailupDroppedModal() {
    this.modalSvc.open(NailupDroppedModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
  }
  openNailupModal() {
    this.modalSvc.open(NailupModalContentComponent, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  openNailupTimeoutModal() {
    this.modalSvc.open(NailupTimeoutModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });
  }

  openRecordSearch() {
    this.state = {
      system: {
        ...this.state.system,
        ...{
          getNewRecord: true,
        },
      },
    };
  }

  openTasksModal() {
    this.coreSvc.state = TeleworkerState.Tasks;
    this.coreSvc.state$.pipe(take(1)).subscribe((currstate) => {
      if (
        [
          TeleworkerState.Unavailable,
          TeleworkerState.UnavailableIB,
          TeleworkerState.UnavailableOB,
          TeleworkerState.Work,
          TeleworkerState.DiallerLogout,
          TeleworkerState.Tasks,
        ].indexOf(currstate!) > -1
      ) {
        this.state = { tasksPresented: true };

        this.modalSvc.open(TasksComponent, {
          size: 'xl',
          backdrop: 'static',
          keyboard: false,
        });
      } else {
        this.toastr.error('Please go Unavailable first.');
      }
    });
  }

  openVoicemailMsg() {
    this.campaignId$
      .pipe(
        take(1),
        withLatestFrom(
          this.presenceSvc.isAuxState$,
          this.ibLaunched$,
          this.obLaunched$
        )
      )
      .subscribe(([campaignId, isAux, ibLaunched, obLaunched]) => {
        if (isAux && !obLaunched && !ibLaunched) {
          if (campaignId === undefined || campaignId === 0) {
            // no campaign selected
            this.toastr.info('Please select a campaign');
          } else {
            this.coreSvc.state = TeleworkerState.Voicemail;
            this.router.navigate(['/work', 'nameandnumber']);
          }
        } else {
          if (obLaunched || ibLaunched)
            this.toastr.info("Please close campaign to view MSG's");
          else this.toastr.info("Please go Unavailable to view MSG's");
        }
      });
  }

  popCallbackScript(recordId: number, campaignId: number) {
    const nowMs = Date.now();
    this.scriptClosed = false;
    this.coreSvc.campaignId = campaignId;

    this.recordSvc
      .getRecord$(campaignId, recordId!)
      .pipe(
        withLatestFrom(
          this.scheduledCampaigns$,
          this.userSvc.userProfile$,
          this.scriptWorker.inputs$()
        )
      )
      .subscribe(([record, scheduledCampaigns, userProfile, inputs]) => {
        const campaign = scheduledCampaigns?.find(
          (c) => c.campaignId === campaignId
        );

        this.state = {
          campaignScriptOpen: true,
          system: {
            queues: this.state.queues,
            navToQ: 1,
            extraData: '',
            userId: userProfile.userId,
            userFirst: userProfile.first,
            userLast: userProfile.last,
            userName: userProfile.first + ' ' + userProfile.last,
            userEmail1: userProfile.email,
            userEmail2: userProfile.email2,
            previousAnswers: { ...record.qFields },
          },
          variables: {},
          master: { ...record.master },
          action: {
            actionType: 'OB',
            startTime: new Date(nowMs),
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
            queueId: 0,
            workId: 0,
            campaignId: campaignId,
            completed: false,
            invoiceId: 0,
            killMonitor: false,
            attempt: -1,
            versionId: campaign?.versionId,
          },
          contact: {
            recording: [],
            //for OB predictive: tw used to have this be when the agent went available. Now it is just going to be the same as dtContactStart
            //for OB preview/callback: dtStart will be when the record opens, and dtContactStart will be overwritten when the call connects
            dtStart: new Date(nowMs),
            dtContactStart: new Date(nowMs),
          },
          answers: record.qFields,
          testMode: false,
          closeDisable: false,
          closeScript: undefined,
          scriptDisable: false,
          waitingforob: false,
          obMessage:
            'Received call for ' +
            record!.master.first +
            ' ' +
            record!.master!.last +
            ', retrieving record ' +
            record!.master?.recordId?.toString(),
          obLaunched: campaign?.actionType.startsWith('OB') || false,
          ibLaunched: campaign?.actionType.startsWith('IB') || false,
          inputs,
        };

        this.coreSvc.state = TeleworkerState.Preview;
        this.router.navigate(['/work', 'contact']);
      });
  }

  popScript(
    campaignId: number,
    recordId?: number,
    DNIS?: string,
    queueId?: string,
    recording?: string,
    extraData?: string,
    waitTime?: number,
    contactscreen: boolean = false,
    phone1: string = 'na'
  ) {
    this.logger.info('WorkService', 'popScript', {
      campaignId,
      recordId,
      DNIS,
      queueId,
      recording,
      extraData,
      waitTime,
      contactscreen,
      phone1,
    })();
    this.scriptClosed = false;

    this.state = { campaignScriptOpen: true };
    if (!waitTime) waitTime = 0;

    var recordingArray: string[] = [];
    if (!!recording) recordingArray = [recording];
    const nowMs = Date.now();

    if (recordId) {
      //implying OB record where recordid is known ahead of time
      this.recordSvc
        .getRecord$(campaignId, recordId!)
        .pipe(
          withLatestFrom(
            this.coreSvc.scriptVersion$,
            this.userSvc.userProfile$,
            this.scriptWorker.inputs$(),
            this.scheduledCampaigns$
          ),
          switchMap(
            ([
              record,
              scriptVersion,
              userProfile,
              inputs,
              scheduledCampaigns,
            ]) => {
              const dialType = scheduledCampaigns?.find(
                (c) => c.campaignId === campaignId
              )?.dialType;
              if (dialType == 'Predictive') {
                return this.getPredictiveRecord$(campaignId, recordId).pipe(
                  map((predictiveRecord) => ({
                    record,
                    scriptVersion,
                    userProfile,
                    inputs,
                    scheduledCampaigns,
                    predictiveRecord,
                  }))
                );
              } else {
                return of({
                  record,
                  scriptVersion,
                  userProfile,
                  inputs,
                  scheduledCampaigns,
                  predictiveRecord: {
                    queueId: queueId ? parseInt(queueId) : undefined,
                    numUsed: '',
                  },
                });
              }
            }
          )
        )
        .subscribe(
          ({
            record,
            scriptVersion,
            userProfile,
            inputs,
            scheduledCampaigns,
            predictiveRecord,
          }) => {
            // this.coreSvc.state = TeleworkerState.Preview;
            let numberused = '';
            const dialType = scheduledCampaigns?.find(
              (c) => c.campaignId === campaignId
            )?.dialType;
            if (dialType == 'Predictive') {
              const { queueId: predictiveQueueId, numUsed } =
                predictiveRecord || {};
              queueId = predictiveQueueId?.toString();
              if (!!numUsed) {
                if (numUsed === record.master.phone1) numberused = 'P1';
                else if (numUsed === record.master.phone2) numberused = 'P2';
                else if (numUsed === record.master.mobile) numberused = 'M1';
              }
            }

            const newState = {
              system: {
                queues: this.state.queues,
                routingAreas: this.state.routingAreas,
                navToQ: 1,
                extraData,
                userId: userProfile.userId,
                userFirst: userProfile.first,
                userLast: userProfile.last,
                userName: userProfile.first + ' ' + userProfile.last,
                userEmail1: userProfile.email,
                userEmail2: userProfile.email2,
                postCallSurveyData: undefined,
                postCallSurveyCancel: undefined,
                previousAnswers: { ...record.qFields },
              },
              variables: {},
              master: { ...record.master },
              action: {
                actionType: 'OB',
                startTime: new Date(nowMs),
                userUtcOffset: 0 - new Date().getTimezoneOffset(),
                queueId: queueId ? parseInt(queueId) : undefined,
                workId: 0,
                numberUsed: numberused,
                campaignId: campaignId,
                completed: false,
                invoiceId: 0,
                killMonitor: false,
                attempt: -1,
                versionId: scriptVersion,
              },
              contact: {
                recording: recordingArray,
                //for OB predictive: tw used to have this be when the agent went available. Now it is just going to be the same as dtContactStart
                //for OB preview/callback: dtStart will be when the record opens, and dtContactStart will be overwritten when the call connects
                dtStart: new Date(nowMs),
                dtContactStart: new Date(nowMs),
              },
              history: undefined,
              answers: record.qFields,
              testMode: false,
              closeDisable: false,
              closeScript: undefined,
              scriptDisable: false,
              postCallSurveyData: undefined,
              postCallSurveyCancel: undefined,
              obLaunched: true,
              inputs,
            };
            this.logger.debug('WorkService', 'popScript', newState)();
            this.state = newState;
          }
        );
    } else {
      let cli = extraData?.split(':')[0]?.replace(/[^0-9*#]/g, '');
      extraData = extraData?.split(':')[1];

      this.coreSvc.campaignId = campaignId;
      this.coreSvc.queueId = +queueId!;
      combineLatest([
        this.scheduledCampaigns$.pipe(take(1)),
        this.userSvc.userProfile$.pipe(take(1)),
        this.scriptWorker.inputs$().pipe(take(1)),
      ]).subscribe(([scheduledCampaigns, userProfile, inputs]) => {
        const campaign = scheduledCampaigns?.find(
          (c) => c.campaignId === campaignId
        );

        this.state = {
          system: {
            navToQ: 1,
            extraData,
            getNewRecord: undefined,
            closeDisable: undefined,
            closeScript: undefined,
            cli: cli,
            queues: this.state.queues,
            routingAreas: this.state.routingAreas,
            startSearch: undefined,
            userId: userProfile.userId,
            userFirst: userProfile.first,
            userLast: userProfile.last,
            userName: userProfile.first + ' ' + userProfile.last,
            userEmail1: userProfile.email,
            userEmail2: userProfile.email2,
            postCallSurveyData: undefined,
            postCallSurveyCancel: undefined,
          },
          variables: {},
          master: {
            phone1: phone1 === 'na' ? cli : phone1,
            campaignId: campaignId,
          },
          action: {
            actionType: DNIS === 'OB' ? 'OB' : 'IB',
            dnis: DNIS === 'OB' ? '' : DNIS,
            queueId: queueId ? +queueId : undefined,
            startTime: new Date(nowMs),
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
            workId: 0,
            campaignId: campaignId,
            completed: false,
            numberUsed: '',
            invoiceId: 0,
            killMonitor: false,
            versionId: campaign?.versionId,
          },
          contact: {
            ...this.state.contact,
            ...{
              recording: recordingArray,
              //tw used to have this be when the agent went available. Now we want it to be how when the call went into the queue.
              //Suspect wait time it tiem it took for agent ot answer, not time in queue time.
              dtStart: new Date(nowMs - 1000 * waitTime!),
            },
            // dtContactStart: new Date(nowMs), // this gets set on the connect event now.
          },
          history: [],
          answers: emptyAnswers,
          testMode: false,
          closeDisable: false,
          closeScript: undefined,
          scriptDisable: false,
          postCallSurveyData: undefined,
          postCallSurveyCancel: undefined,
          obLaunched: campaign?.actionType.startsWith('OB') || false,
          ibLaunched: campaign?.actionType.startsWith('IB') || false,
          inputs,
        };

        this.router.navigate(['/work', 'script']);
      });
    }
  }

  popTestScript(
    campaignId: number,
    versionId: number,
    questionId: number,
    uatFlag: boolean = false
  ) {
    this.state = { omniChannel: false };
    this.logger.debug('WorkService', 'popTestScript', {
      campaignId,
      versionId,
      questionId,
    })();

    this.scriptClosed = false;

    this.state = { dialType: 'IB' };

    this.coreSvc.campaignId = campaignId;
    const nowMs = Date.now();

    this.scriptWorker.loadWorkers([{ campaignId, versionId }]);

    combineLatest([
      this.getActionTypeForCampaign(campaignId),
      this.userSvc.userProfile$,
      this.getCampaignQueues$(campaignId),
      this.scriptWorker.inputs$(),
    ]).subscribe(([c, userProfile, campaignQueues, inputs]) => {
      this.logger.debug('WorkService', 'popTestScript.subscribe', {
        c,
        userProfile,
        campaignQueues,
      })();
      if (c.actionType === 'IB') {
        this.state = {
          variables: {},
          master: {
            phone1: '',
            campaignId: campaignId,
          },
          system: {
            ...this.state.system,
            navToQ: questionId,
            userId: userProfile.userId,
            userFirst: userProfile.first,
            userLast: userProfile.last,
            userName: userProfile.first + ' ' + userProfile.last,
            userEmail1: userProfile.email,
            userEmail2: userProfile.email2,
            queues: campaignQueues,
          },
          action: {
            actionType: 'IB',
            dnis: 'Testing',
            queueId: 0,
            startTime: new Date(nowMs),
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
            workId: 0,
            campaignId: campaignId,
            completed: false,
            numberUsed: '',
            invoiceId: 0,
            killMonitor: false,
            versionId: versionId,
          },
          contact: {
            recording: [],
            dtStart: new Date(nowMs),
            dtContactStart: new Date(nowMs),
          },
          history: [],
          answers: emptyAnswers,
          testMode: !uatFlag,
          closeDisable: false,
          closeScript: undefined,
          scriptDisable: false,
          queues: campaignQueues,
          inputs,
        };
      } else {
        this.state = {
          variables: {},
          master: {
            sal: 'Mr',
            first: 'Script',
            last: 'Tester',
            address1: '14 Test Street',
            suburb: 'Testville',
            state: 'NSW',
            postcode: '2000',
            recordId: -1,
            campaignId: campaignId,
            phone1: '0200000000',
            phone2: '0200000001',
            mobile: '0400000002',
            email1: 'script.tester@email.com',
            u20: 'u20value',
            stickyNotes: 'This is the sticky note...',
          },
          action: {
            actionType: c.actionType,
            dnis: 'Testing',
            queueId: 0,
            startTime: new Date(nowMs),
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
            workId: 0,
            campaignId: campaignId,
            completed: false,
            numberUsed: '',
            invoiceId: 0,
            killMonitor: false,
          },
          contact: {
            recording: [],
            dtStart: new Date(nowMs),
            dtContactStart: new Date(nowMs),
          },
          system: {
            ...this.state.system,
            navToQ: questionId,
            userId: userProfile.userId,
            userFirst: userProfile.first,
            userLast: userProfile.last,
            userName: userProfile.first + ' ' + userProfile.last,
            userEmail1: userProfile.email,
            userEmail2: userProfile.email2,
          },
          history: [
            {
              campaignId,
              attempt: 1,
              datetime: new Date(nowMs - 6 * 24 * 60 * 60 * 1000),
              outcome: 'No Answer',
              completed: false,
              userId: 1222,
              notes: 'This is a short note',
              clientNotes:
                'Long note example: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            } as ActionDto,
            {
              campaignId,
              attempt: 2,
              datetime: new Date(nowMs - 3 * 24 * 60 * 60 * 1000),
              outcome: 'Engaged',
              completed: false,
              userId: 1222,
              notes: 'This is a short note',
              clientNotes:
                'Long note example: Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.',
            } as ActionDto,
          ],
          answers: emptyAnswers,
          testMode: !uatFlag,
          inputs,
        };
      }
      this.router.navigate(['/work', 'script']);
    });
  }

  popWaitingOBCall() {
    this.waitingobcall$
      .pipe(
        filter((wobc) => !!wobc),
        take(1)
      )
      .subscribe((wobc) => {
        this.state = { waitingobcall: undefined };
        this.popScript(
          wobc?.campaignId!,
          wobc?.recordId,
          wobc?.DNIS,
          wobc?.queueId,
          wobc?.recording,
          wobc?.extraData,
          wobc?.waitTime,
          wobc?.contactscreen
        );
        this.navigateToScript();
      });
  }

  recordQAHistory(qaId: number, recordId: number) {
    var requestBody: RecordQAHistoryRequest = {
      qaId: qaId,
      recordId: recordId,
    };
    return this.http.post(`${this.campaignApi}/qahistory`, requestBody);
  }

  releaseNameAndNumber(sessionid: string) {
    this.http
      .put<any>(`${this.recordsApi}/nameandnumber/release/${sessionid}`, '')
      .subscribe();
  }

  reloadCampaigns() {
    this.refreshImmediately.next();
  }

  rescheduleNameAndNumber(cmd: RescheduleNameandnumberCommand) {
    this.http
      .put<any>(`${this.recordsApi}/nameandnumber/reschedule`, cmd)
      .subscribe();
  }

  resetWaitForCall() {
    this.state = {
      waitingforob: false,
      oncall: false,
    };
    //this.router.navigate(['/work']);
  }

  retryEmptyQueue() {
    this.state = {
      obMessage: 'Queue Empty, will retry again in 10 seconds...',
    };
  }

  routeToNews() {
    this.router.navigate(['/news']);
  }

  routeToOBWaiting() {
    this.router.navigate(['/work', 'obwaiting']);
  }

  routeToScript(recording: string) {
    // this is the situation where a preview call has been made.
    // we need to update the recording and then navigate to scripting (from the contact page)
    this.addRecording(recording);

    this.router.navigate(['/work', 'script']);
  }

  selectCampaign(campaignId: number) {
    const { obLaunched, msgs } = this.state;
    this.coreSvc.campaignId = campaignId;
    if (obLaunched) {
      // need to cancel getNextNumber
    }

    this.state = {
      omniChannel: false,
      obLaunched: false,
      dialType: undefined,
      ibLaunched: false,
    };
  }

  setAgent3WayState(newstate: string) {
    this.state = {
      system: { ...this.state.system, ...{ agent3WayState: newstate } },
    };
  }

  setCancelMessage() {
    this.state = { obMessage: 'Closing', oncall: false };
  }

  setContactDtStart(waitTime: number) {
    const nowMs = Date.now();
    const { contact } = this.state;
    this.state = {
      contact: {
        ...contact,
        ...{
          dtStart: new Date(nowMs - 1000 * waitTime!),
          dtContactStart: new Date(nowMs),
        },
      },
    };
  }

  setContactStart() {
    const nowMs = Date.now();
    const { contact } = this.state;
    if (!contact?.dtContactStart) {
      //contact hasn't occured yet
      this.state = {
        contact: {
          ...contact,
          ...{ dtContactStart: new Date(nowMs) },
        },
      };
    }
  }
  setNailupTimeLimitIB() {
    this.scheduledCampaigns$.pipe(take(1)).subscribe((scheduledCampaigns) => {
      let nailupTimeLimit = -1;
      scheduledCampaigns?.forEach((campaign) => {
        this.coreSvc.scriptVersion = campaign?.versionId;

        if (
          campaign?.nailupTimeLimit > 0 &&
          (nailupTimeLimit == -1 || campaign?.nailupTimeLimit < nailupTimeLimit)
        )
          nailupTimeLimit = campaign?.nailupTimeLimit;
      });

      this.state = {
        nailupTimeLimit: nailupTimeLimit,
      };
    });
  }

  setNumberUsed(phoneNumber: string) {
    if (!phoneNumber) return;

    const { master } = this.state;
    let numUsed = '';

    if (master?.phone1 === phoneNumber) numUsed = 'P1';
    else if (master?.phone2 === phoneNumber) numUsed = 'P2';
    else if (master?.mobile === phoneNumber) numUsed = 'M1';

    this.state = {
      action: {
        ...this.state.action,
        ...{ numberUsed: numUsed },
      },
    };
  }

  setPhone1(phone: string) {
    this.state = {
      master: {
        ...this.state.master,
        ...{ phone1: phone },
      },
    };
  }

  setTasksPresented(flag: boolean) {
    this.state = { tasksPresented: flag };
  }

  setUnavailableToTakeCalls() {
    this.state = {
      waitingforob: false,
      obMessage:
        'Your nailup call has dropped and you are unavailable to take calls. Press Continue to reconnect',
      obContinue: true,
    };
  }

  setWaitingForOB(flag: boolean) {
    this.state = {
      waitingforob: flag,
      obMessage: 'Waiting for call',
      oncall: flag === true ? false : true,
      obContinue: false,
    };

    this.routeToOBWaiting();
  }

  setWork() {
    this.coreSvc.state = TeleworkerState.Work;
    this.coreSvc.sidebarMenu = WorkMenuComponent;
    this.state = { allowBack: false, allowRefresh: false };
    this.reloadCampaignsOnInterval();
  }

  setWrapStart() {
    this.state = {
      contact: {
        ...this.state.contact,
        ...{ dtWrapStart: new Date(Date.now()) },
      },
    };
  }

  showStateModal() {
    const modalRef = this.modalSvc.open(UnavailablePresenceStateComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
    });
    modalRef.componentInstance.modalRef = modalRef;
  }

  showVerifyCloseModal() {
    this.modalSvc.open(VerifyCloseModalComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
    });
  }

  storeOBCall(
    campaignId: number,
    recordId?: number,
    DNIS?: string,
    queueId?: string,
    recording?: string,
    extraData?: string,
    waitTime?: number,
    contactscreen: boolean = false
  ) {
    this.state = {
      waitingobcall: {
        campaignId,
        recordId,
        DNIS,
        queueId,
        recording,
        extraData,
        waitTime,
        contactscreen,
      },
      waitingforob: false,
      obMessage: `Received call for ${extraData?.replace(
        '....',
        ''
      )}, retrieving record ${recordId?.toString()}`,
    };
  }

  submitEmptyRecordRecording() {
    this.coreSvc.campaignId$.pipe(take(1)).subscribe((campaignId) => {
      let mediaArray =
        this.state.contact?.recording?.map(
          (mediaType) =>
            ({
              archived: false,
              checked: false,
              datetime: new Date(Date.now()),
              duration: 0,
              killMonitor: false,
              mediaType,
              released: false,
            } as MediaDto)
        ) || [];

      if (this.recordingPlaceolders.length > 0) {
        this.recordingPlaceolders.forEach((recording) => {
          mediaArray.push({
            archived: false,
            checked: false,
            datetime: new Date(Date.now()),
            duration: 0,
            killMonitor: false,
            mediaType: recording,
            released: false,
          });
        });
      }

      this.recordingPlaceolders = [];

      if (!!mediaArray && mediaArray.length > 0) {
        var recordToSubmit: RecordSubmitDto = {
          action: {
            outcome: '',
            completed: true,
            attempt: 0,
            batchId: 0,
            actionType: 'OB',
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
          },
          master: undefined,
          contactLog: undefined,
          media: mediaArray,
          recordId: 0,
          qFields: [],
          campaignId,
        };

        this.uploadRecord(recordToSubmit).subscribe();
      }
    });
  }

  submitFastVoicemailRecord(): Observable<boolean> {
    return this.waitingobcall$.pipe(
      filter((wobc) => !!wobc),
      take(1),
      switchMap((wobc) =>
        this.getPredictiveRecord$(wobc?.campaignId!, wobc?.recordId!).pipe(
          map((predictiverecord) => ({ wobc, predictiverecord }))
        )
      ),
      withLatestFrom(this.coreSvc.scriptVersion$),
      switchMap(([{ wobc, predictiverecord }, scriptVersion]) => {
        let numberused = '';

        var recordToSubmit: RecordSubmitDto = {
          action: {
            actionType: 'OB',
            startTime: new Date(Date.now()),
            endTime: new Date(Date.now()),
            userUtcOffset: 0 - new Date().getTimezoneOffset(),
            queueId: predictiverecord?.queueId,
            workId: 0,
            numberUsed: numberused,
            campaignId: wobc?.campaignId,
            completed: false,
            invoiceId: 0,
            killMonitor: false,
            attempt: 0,
            versionId: scriptVersion,
            outcome: 'FastVoicemail',
          },
          campaignId: wobc?.campaignId,
          master: undefined,
          contactLog: undefined,
          qFields: [],
          media: [
            {
              archived: false,
              checked: false,
              datetime: new Date(Date.now()),
              duration: 0,
              killMonitor: false,
              mediaType: wobc?.recording,
              released: false,
            },
          ],
          recordId: wobc?.recordId,
        };

        return this.uploadRecord(recordToSubmit).pipe(
          tap(() => {
            this.closeRecord();
            this.state = {
              waitingobcall: undefined,
              obMessage: 'Waiting for call...',
              oncall: false,
            };
          })
        );
      })
    );
  }

  submitRecord(): Observable<boolean> {
    const {
      system,
      master,
      action,
      answers,
      contact,
      history,
      scheduledCampaigns,
      pendingCampaigns,
    } = this.state;
    const nowMs = Date.now();

    const faileUploadItem = this.fetchFailUpload();
    if (faileUploadItem) {
      return this.uploadRecord(
        faileUploadItem.record,
        faileUploadItem.attempts,
        true
      );
    }

    if (!master) {
      //noting to submit
      return of(false);
    }

    if (action) {
      action.endTime = new Date(nowMs);
      if (contact && !contact?.dtWrapStart) {
        contact.dtWrapStart = new Date(nowMs);
      }

      if (contact && !contact?.dtContactStart) {
        // we have popped a script but we didn't connect a call
        // set dtWrapStart and dtContactStart contact fields to dtStart
        contact.dtWrapStart = contact.dtStart;
        contact.dtContactStart = contact.dtStart;
      }

      if (action.startTime && action.endTime) {
        action.duration = Math.round(
          (action.endTime.getTime() - action.startTime.getTime()) / 1000
        );
      }

      const attempts =
        history?.filter(
          (act) =>
            act.actionType === 'OB' && act.campaignId === master.campaignId
        ).length || 0;
      if (!action.outcome) {
        action.attempt = undefined;
      } else {
        action.attempt = attempts + 1;

        const campaign = scheduledCampaigns?.find(
          (c) => c.campaignId === action?.campaignId
        );
        if (
          !system?.skipNoContactCheck &&
          !action?.completed &&
          campaign?.actionType === 'OBT' &&
          action.attempt >= campaign.noContactLimit
        ) {
          action.outcome = 'NoContact';
          action.completed = true;
        }

        const outcome = campaign?.outcomes.find(
          (o) => o.outcome == action.outcome
        );
        action.rateRevenue = outcome?.rateRevenue;
        if (!action.rateUser) {
          action.rateUser = outcome?.rateUser;
        }
      }
    }

    const qafields =
      answers
        ?.map((answer, qNo) => ({ qNo, answer } as QuestionAnswerDto))
        ?.slice(1)
        ?.filter((qaf) => !!qaf.answer) || [];

    let recordingDuration = 0;
    if (contact && contact.dtContactStart && contact.dtWrapStart) {
      recordingDuration = Math.round(
        (contact.dtWrapStart.getTime() - contact.dtContactStart.getTime()) /
          1000
      );
    }

    let mediaArray =
      contact?.recording?.map(
        (mediaType) =>
          ({
            archived: false,
            checked: false,
            datetime: new Date(nowMs),
            duration: recordingDuration,
            killMonitor: action?.killMonitor,
            mediaType,
            released: false,
          } as MediaDto)
      ) || [];

    if (this.recordingPlaceolders.length > 0) {
      this.recordingPlaceolders.forEach((recording) => {
        mediaArray.push({
          archived: false,
          checked: false,
          datetime: new Date(Date.now()),
          duration: recordingDuration,
          killMonitor: action?.killMonitor,
          mediaType: recording,
          released: false,
        });
      });
    }

    this.recordingPlaceolders = [];

    var recordToSubmit: RecordSubmitDto = {
      action: action,
      campaignId: action?.campaignId,
      contactLog: {
        dtStart: contact?.dtStart || new Date(nowMs),
        dtContactStart: contact?.dtContactStart || new Date(nowMs),
        dtWrapStart: contact?.dtWrapStart || new Date(nowMs),
        dtFinish: action?.endTime || new Date(nowMs),
      },
      master: {
        ...master,
        u2: master.u2
          ? (formatDate(master.u2, 'yyyy-MM-ddTHH:mm:ss.SSS', 'en') as any) //hack to have date be timezone-less
          : undefined,
      },
      media: mediaArray,
      qFields: qafields,
      recordId: master?.recordId,
    };

    this.postCallSurveyData$
      .pipe(withLatestFrom(this.master$, this.defaultTenantId$))
      .subscribe(([pcsdata, master, tid]) => {
        this.state = {
          postCallSurveyPhone: master?.phone1,
          postCallSurveyRecordId: master?.recordId,
          postCallSurveyCampaign: master?.campaignId,
        };
      });

    if (pendingCampaigns !== undefined) {
      this.setScheduledCampaigns(pendingCampaigns);
    }

    return this.uploadRecord(recordToSubmit, 0, true);
  }

  unlaunchOB() {
    this.coreSvc.campaignId = 0;
    this.state = {
      obLaunched: false,
    };
  }

  unsetWork() {
    this.state = { allowBack: true, allowRefresh: true };
    this.refreshSubs?.forEach((rs) => rs.unsubscribe());
    this.refreshSubs = undefined;
    this.callbackSvc.loadUserCallbacks().subscribe();
  }

  updateAnswers(answers: Answers) {
    this.state = {
      answers: answers,
    };
  }

  updateIbLaunched(launched: boolean) {
    this.state = {
      ibLaunched: launched,
      dialType: launched ? 'IB' : undefined,
    };
  }

  updateMasterContact(master: Partial<Master>) {
    this.state = {
      master: {
        ...this.state.master,
        ...master,
      },
    };
  }

  updateMsgButtonState(newStatus: boolean) {
    this.state = {
      hasNameAndNumber: newStatus,
    };
  }

  updateNotes(notes: string, clientNotes: string) {
    this.state = {
      action: {
        ...this.state.action,
        ...{
          notes,
          clientNotes,
        },
      },
    };
  }

  updateStateToGeneral() {
    this.coreSvc.state = TeleworkerState.Work;
    this.coreSvc.campaignId = 0;
    this.state = { obLaunched: false, ibLaunched: false };
  }
}
